import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { ArrowRight, Breadcrumbs, Button, PageHeader } from "@opendate-app/setlist";

import { useLandingPage } from "./LandingPageBuilderContainer";
import PagePreview from "./PagePreview";
import { BuilderIdentity, ProgressTrack } from "../shared/BuilderHeader";
import VenueStep from "./steps/VenueStep";
import TemplateStep from "./steps/TemplateStep";
import ContentStep from "./steps/ContentStep";
import SignupsStep from "./steps/SignupsStep";
import PublishStep from "./steps/PublishStep";
import { LANDING_PAGE_STEPS, completedLandingPageSteps, initialLandingPageStep, readyToPublish, stepReachable, furthestReachableStep } from "./landingPageSteps";

const STEP_COMPONENTS = {
  venue: VenueStep,
  template: TemplateStep,
  content: ContentStep,
  signups: SignupsStep,
  publish: PublishStep,
};

const FULL_WIDTH_STEPS = ["venue", "template"];

const styles = {
  page: {
    display: "flex",
    flexDirection: "column",
    minHeight: 560,
    overflow: "hidden",
    background: "var(--sl-bg-primary)",
    color: "var(--sl-text-primary)",
    border: "1px solid var(--sl-border-secondary)",
    borderRadius: "var(--sl-radius-xl, 12px)",
  },
  shell: { display: "flex", flexDirection: "column", gap: 24 },
  body: { flex: 1, display: "flex", minHeight: 0 },
  formColumn: {
    width: 520,
    flexShrink: 0,
    overflowY: "auto",
    padding: 32,
    borderRight: "1px solid var(--sl-border-secondary)",
  },
  fullWidth: { flex: 1, overflowY: "auto", padding: 32 },
  previewPane: { flex: 1, minWidth: 0, background: "var(--sl-bg-tertiary)" },
  title: { margin: 0, fontSize: 20, fontWeight: 600, color: "var(--sl-text-primary)" },
  subtitle: { margin: "4px 0 0", fontSize: 14, color: "var(--sl-text-tertiary)" },
};

const LandingPageBuilder = () => {
  const { page, props, loadVenueData, savePage, publish, waitForSaves } = useLandingPage();
  const [step, setStep] = useState(() => {
    const requested = initialLandingPageStep(window.location.search);
    return stepReachable(props.landingPage, requested) ? requested : furthestReachableStep(props.landingPage);
  });
  const [name, setName] = useState(page.name);
  const [fullEditor, setFullEditor] = useState(false);
  const pageRef = useRef(null);
  const [pageHeight, setPageHeight] = useState(null);

  useLayoutEffect(() => {
    const fitToViewport = () => {
      setPageHeight(window.innerHeight - pageRef.current.getBoundingClientRect().top - window.scrollY - 16);
    };

    fitToViewport();
    window.addEventListener("resize", fitToViewport);
    return () => window.removeEventListener("resize", fitToViewport);
  }, []);

  useEffect(() => {
    const url = new URL(window.location.href);
    url.searchParams.set("step", step);
    window.history.replaceState(null, "", url);
  }, [step]);

  useEffect(() => {
    if (step !== "content") setFullEditor(false);
  }, [step]);

  useEffect(() => {
    loadVenueData(page.venue_ownership_id);
  }, [page.venue_ownership_id]);

  useEffect(() => {
    setName(page.name);
  }, [page.name]);

  const completed = completedLandingPageSteps(page);
  const stepIndex = LANDING_PAGE_STEPS.findIndex((item) => item.value === step);
  const currentStep = LANDING_PAGE_STEPS[stepIndex];
  const isLastStep = stepIndex === LANDING_PAGE_STEPS.length - 1;
  const published = page.status === "published";
  const StepComponent = STEP_COMPONENTS[step];
  const editing = step === "content" && fullEditor;
  const fullWidth = FULL_WIDTH_STEPS.includes(step) || editing;

  const nextDisabled = isLastStep
    ? published || !readyToPublish(page)
    : !stepReachable(page, LANDING_PAGE_STEPS[stepIndex + 1].value);

  const goTo = (value) => {
    if (stepReachable(page, value)) setStep(value);
  };

  const saveName = () => {
    const trimmed = name.trim();

    if (!trimmed) {
      setName(page.name);
      return;
    }

    if (trimmed !== page.name) savePage({ name: trimmed });
  };

  const closeBuilder = () => waitForSaves().then((saved) => {
    if (saved === false) return;
    window.location.href = `/teams/${props.team.id}/pages`;
  });

  const stepHeading = (
    <div>
      <h1 style={styles.title}>{currentStep.title}</h1>
      <p style={styles.subtitle}>{currentStep.subtitle}</p>
    </div>
  );

  return (
    <div style={styles.shell}>
      <PageHeader
        breadcrumbs={<Breadcrumbs items={[{ label: "Pages", onClick: closeBuilder }, { label: name || "Untitled page" }]} />}
        title={(
          <BuilderIdentity
            noun="page"
            caption={`Page${page.venue_name ? ` · ${page.venue_name}` : ""}`}
            name={name}
            setName={setName}
            onSave={saveName}
            onCancel={() => setName(page.name)}
            inline
          />
        )}
        supportingText={<ProgressTrack ariaLabel="Page steps" steps={LANDING_PAGE_STEPS} step={step} completed={completed} onSelect={goTo} inline />}
        actions={(
          <>
            {stepIndex > 0 && (
              <Button variant="secondary" onClick={() => setStep(LANDING_PAGE_STEPS[stepIndex - 1].value)}>
                Back
              </Button>
            )}
            <Button
              variant="primary"
              trailingIcon={isLastStep ? undefined : ArrowRight}
              disabled={nextDisabled}
              onClick={() => (isLastStep ? publish() : setStep(LANDING_PAGE_STEPS[stepIndex + 1].value))}
            >
              {isLastStep ? (published ? "Published" : "Publish") : `Next: ${LANDING_PAGE_STEPS[stepIndex + 1].label}`}
            </Button>
          </>
        )}
      />

      <div ref={pageRef} style={{ ...styles.page, height: pageHeight }}>
        <main style={styles.body}>
          {fullWidth ? (
            <section style={styles.fullWidth}>
              {!editing && stepHeading}
              <StepComponent setStep={setStep} fullEditor={fullEditor} setFullEditor={setFullEditor} />
            </section>
          ) : (
            <>
              <section style={styles.formColumn}>
                {stepHeading}
                <StepComponent setStep={setStep} fullEditor={fullEditor} setFullEditor={setFullEditor} />
              </section>
              <aside style={styles.previewPane} aria-label="Page preview">
                <PagePreview />
              </aside>
            </>
          )}
        </main>
      </div>
    </div>
  );
};

export default LandingPageBuilder;
